import { defaultRegistry, WebcvtError } from '@catlabtech/webcvt-core';

// src/constants.ts
var MAX_INPUT_BYTES = 200 * 1024 * 1024;
var MAX_ENTRY_UNCOMPRESSED_BYTES = 256 * 1024 * 1024;
var MAX_TOTAL_UNCOMPRESSED_BYTES = 512 * 1024 * 1024;
var MAX_COMPRESSION_RATIO = 1e3;
var MAX_ZIP_ENTRIES = 65536;
var MAX_TAR_ENTRIES = 65536;
var MAX_ZIP_COMMENT_BYTES = 4096;
var MAX_ZIP_NAME_BYTES = 4096;
var MAX_TAR_NAME_BYTES = 255;
var ZIP_LOCAL_HEADER_SIG = 67324752;
var ZIP_CENTRAL_DIR_SIG = 33639248;
var ZIP_EOCD_SIG = 101010256;
var ZIP_EOCD_FIXED_SIZE = 22;
var ZIP_LOCAL_HEADER_FIXED_SIZE = 30;
var ZIP_CENTRAL_DIR_FIXED_SIZE = 46;
var ZIP_METHOD_STORED = 0;
var ZIP_METHOD_DEFLATE = 8;
var ZIP_FLAG_ENCRYPTED = 1;
var ZIP_FLAG_UTF8 = 2048;
var ZIP64_SENTINEL_U16 = 65535;
var ZIP64_SENTINEL_U32 = 4294967295;
var TAR_BLOCK_SIZE = 512;
var TAR_OFF_NAME = 0;
var TAR_LEN_NAME = 100;
var TAR_OFF_MODE = 100;
var TAR_LEN_MODE = 8;
var TAR_OFF_SIZE = 124;
var TAR_LEN_SIZE = 12;
var TAR_OFF_MTIME = 136;
var TAR_LEN_MTIME = 12;
var TAR_OFF_CHKSUM = 148;
var TAR_LEN_CHKSUM = 8;
var TAR_OFF_TYPEFLAG = 156;
var TAR_OFF_MAGIC = 257;
var TAR_LEN_MAGIC = 6;
var TAR_OFF_VERSION = 263;
var TAR_LEN_VERSION = 2;
var TAR_OFF_UNAME = 265;
var TAR_LEN_UNAME = 32;
var TAR_OFF_GNAME = 297;
var TAR_LEN_GNAME = 32;
var TAR_OFF_PREFIX = 345;
var TAR_LEN_PREFIX = 155;
var TAR_TYPEFLAG_FILE_NUL = "\0";
var TAR_TYPEFLAG_FILE = "0";
var TAR_TYPEFLAG_DIRECTORY = "5";
var TAR_TYPEFLAG_PAX_EXTENDED = "x";
var TAR_TYPEFLAG_PAX_GLOBAL = "g";
var TAR_MAGIC = "ustar\0";
var TAR_VERSION = "00";
var GZIP_MAGIC_0 = 31;
var GZIP_MAGIC_1 = 139;
var BZ2_MAGIC = [66, 90, 104];
var XZ_MAGIC = [253, 55, 122, 88, 90, 0];
var ZIP_MIME = "application/zip";
var TAR_MIME = "application/x-tar";
var GZIP_MIME = "application/gzip";
var TGZ_MIME = "application/gzip";
var BZ2_MIME = "application/x-bzip2";
var XZ_MIME = "application/x-xz";
var ZIP_COMPRESS_THRESHOLD = 64;
var ZIP_VERSION_NEEDED = 20;
var ZIP_VERSION_MADE_BY = 788;
var ArchiveInputTooLargeError = class extends WebcvtError {
  constructor(size, max) {
    super(
      "ARCHIVE_INPUT_TOO_LARGE",
      `Archive input is ${size} bytes; maximum supported is ${max} bytes (200 MiB).`
    );
    this.name = "ArchiveInputTooLargeError";
  }
};
var ArchiveInvalidEntryNameError = class extends WebcvtError {
  constructor(name, reason) {
    super("ARCHIVE_INVALID_ENTRY_NAME", `Entry name "${name}" is invalid: ${reason}`);
    this.name = "ArchiveInvalidEntryNameError";
  }
};
var ArchiveEntrySizeCapError = class extends WebcvtError {
  constructor(name, size, cap) {
    super(
      "ARCHIVE_ENTRY_SIZE_CAP",
      `Entry "${name}" uncompressed size ${size} exceeds the per-entry cap of ${cap} bytes (256 MiB).`
    );
    this.name = "ArchiveEntrySizeCapError";
  }
};
var ArchiveTotalSizeCapError = class extends WebcvtError {
  constructor(total, cap) {
    super(
      "ARCHIVE_TOTAL_SIZE_CAP",
      `Cumulative uncompressed size ${total} exceeds the total cap of ${cap} bytes (512 MiB).`
    );
    this.name = "ArchiveTotalSizeCapError";
  }
};
var ArchiveBz2NotSupportedError = class extends WebcvtError {
  constructor() {
    super(
      "ARCHIVE_BZ2_NOT_SUPPORTED",
      "bzip2 archives are not natively supported. Install @catlabtech/webcvt-backend-wasm for bzip2 support."
    );
    this.name = "ArchiveBz2NotSupportedError";
  }
};
var ArchiveXzNotSupportedError = class extends WebcvtError {
  constructor() {
    super(
      "ARCHIVE_XZ_NOT_SUPPORTED",
      "xz archives are not natively supported. Install @catlabtech/webcvt-backend-wasm for xz support."
    );
    this.name = "ArchiveXzNotSupportedError";
  }
};
var ZipTooShortError = class extends WebcvtError {
  constructor(size) {
    super("ZIP_TOO_SHORT", `Input is ${size} bytes; minimum ZIP size is 22 bytes (EOCD only).`);
    this.name = "ZipTooShortError";
  }
};
var ZipNoEocdError = class extends WebcvtError {
  constructor() {
    super(
      "ZIP_NO_EOCD",
      "No ZIP End-of-Central-Directory (EOCD) signature found. Not a valid ZIP file, or comment exceeds 4 KiB."
    );
    this.name = "ZipNoEocdError";
  }
};
var ZipCommentTooLargeError = class extends WebcvtError {
  constructor(commentLength) {
    super(
      "ZIP_COMMENT_TOO_LARGE",
      `ZIP file comment is ${commentLength} bytes; maximum supported is 4096 bytes.`
    );
    this.name = "ZipCommentTooLargeError";
  }
};
var ZipNotZip64SupportedError = class extends WebcvtError {
  constructor() {
    super(
      "ZIP_NOT_ZIP64_SUPPORTED",
      "ZIP64 archives are not supported in first pass. Support is planned for Phase 4.5."
    );
    this.name = "ZipNotZip64SupportedError";
  }
};
var ZipMultiDiskNotSupportedError = class extends WebcvtError {
  constructor(diskNumber) {
    super(
      "ZIP_MULTI_DISK_NOT_SUPPORTED",
      `Multi-disk ZIP archives are not supported (disk number = ${diskNumber}).`
    );
    this.name = "ZipMultiDiskNotSupportedError";
  }
};
var ZipBadCentralDirectoryError = class extends WebcvtError {
  constructor(offset, sig) {
    super(
      "ZIP_BAD_CENTRAL_DIRECTORY",
      `Expected central directory signature 0x02014b50 at offset ${offset}, got 0x${sig.toString(16)}.`
    );
    this.name = "ZipBadCentralDirectoryError";
  }
};
var ZipBadLocalHeaderError = class extends WebcvtError {
  constructor(offset, sig) {
    super(
      "ZIP_BAD_LOCAL_HEADER",
      `Expected local file header signature 0x04034b50 at offset ${offset}, got 0x${sig.toString(16)}.`
    );
    this.name = "ZipBadLocalHeaderError";
  }
};
var ZipEncryptedNotSupportedError = class extends WebcvtError {
  constructor(name) {
    super(
      "ZIP_ENCRYPTED_NOT_SUPPORTED",
      `ZIP entry "${name}" is encrypted. Encrypted ZIP entries are not supported in first pass.`
    );
    this.name = "ZipEncryptedNotSupportedError";
  }
};
var ZipUnsupportedMethodError = class extends WebcvtError {
  constructor(name, method) {
    super(
      "ZIP_UNSUPPORTED_METHOD",
      `ZIP entry "${name}" uses compression method ${method}. Only methods 0 (stored) and 8 (deflate) are supported.`
    );
    this.name = "ZipUnsupportedMethodError";
  }
};
var ZipChecksumError = class extends WebcvtError {
  constructor(name, expected, got) {
    super(
      "ZIP_CHECKSUM_ERROR",
      `CRC-32 mismatch for ZIP entry "${name}": expected 0x${expected.toString(16).padStart(8, "0")}, got 0x${got.toString(16).padStart(8, "0")}.`
    );
    this.name = "ZipChecksumError";
  }
};
var ZipCompressionRatioError = class extends WebcvtError {
  constructor(name, ratio) {
    super(
      "ZIP_COMPRESSION_RATIO_ERROR",
      `ZIP entry "${name}" has a compression ratio of ${ratio}:1, which exceeds the maximum of 1000:1. Potential zip bomb rejected.`
    );
    this.name = "ZipCompressionRatioError";
  }
};
var ZipTooManyEntriesError = class extends WebcvtError {
  constructor(count, max) {
    super("ZIP_TOO_MANY_ENTRIES", `ZIP archive has ${count} entries; maximum supported is ${max}.`);
    this.name = "ZipTooManyEntriesError";
  }
};
var ZipCorruptStreamError = class extends WebcvtError {
  constructor(reason) {
    super("ZIP_CORRUPT_STREAM", `ZIP stream is corrupt: ${reason}`);
    this.name = "ZipCorruptStreamError";
  }
};
var ZipTruncatedEntryError = class extends WebcvtError {
  constructor(name, payloadOffset, compressedSize, inputLength) {
    super(
      "ZIP_TRUNCATED_ENTRY",
      `ZIP entry "${name}" payload at offset ${payloadOffset} with compressed size ${compressedSize} extends past input end (${inputLength} bytes).`
    );
    this.name = "ZipTruncatedEntryError";
  }
};
var TarMisalignedInputError = class extends WebcvtError {
  constructor(size) {
    super(
      "TAR_MISALIGNED_INPUT",
      `TAR input size ${size} is not a multiple of 512. Not a valid TAR archive.`
    );
    this.name = "TarMisalignedInputError";
  }
};
var TarTooShortError = class extends WebcvtError {
  constructor(size) {
    super(
      "TAR_TOO_SHORT",
      `TAR input is ${size} bytes; minimum is 1024 bytes (end-of-archive marker).`
    );
    this.name = "TarTooShortError";
  }
};
var TarChecksumError = class extends WebcvtError {
  constructor(offset, expected, got) {
    super(
      "TAR_CHECKSUM_ERROR",
      `TAR header checksum mismatch at offset ${offset}: expected ${expected}, got ${got}.`
    );
    this.name = "TarChecksumError";
  }
};
var TarNonUstarNotSupportedError = class extends WebcvtError {
  constructor(offset, magic) {
    super(
      "TAR_NON_USTAR_NOT_SUPPORTED",
      `TAR header at offset ${offset} does not have ustar magic (got "${magic}"). Only POSIX ustar format is supported.`
    );
    this.name = "TarNonUstarNotSupportedError";
  }
};
var TarGnuVariantNotSupportedError = class extends WebcvtError {
  constructor(offset) {
    super(
      "TAR_GNU_VARIANT_NOT_SUPPORTED",
      `TAR header at offset ${offset} uses GNU tar format ("ustar  " variant). Only POSIX ustar is supported.`
    );
    this.name = "TarGnuVariantNotSupportedError";
  }
};
var TarUnsupportedTypeflagError = class extends WebcvtError {
  constructor(typeflag, entryName) {
    super(
      "TAR_UNSUPPORTED_TYPEFLAG",
      `TAR entry "${entryName}" has unsupported typeflag '${typeflag}'. Only regular files ('0'/NUL) and directories ('5') are supported.`
    );
    this.name = "TarUnsupportedTypeflagError";
  }
};
var TarPaxNotSupportedError = class extends WebcvtError {
  constructor(typeflag) {
    super(
      "TAR_PAX_NOT_SUPPORTED",
      `TAR PAX extended header (typeflag '${typeflag}') is not supported in first pass.`
    );
    this.name = "TarPaxNotSupportedError";
  }
};
var TarTooManyEntriesError = class extends WebcvtError {
  constructor(max) {
    super("TAR_TOO_MANY_ENTRIES", `TAR archive entry count exceeds maximum of ${max}.`);
    this.name = "TarTooManyEntriesError";
  }
};
var TarBase256SizeNotSupportedError = class extends WebcvtError {
  constructor(entryName) {
    super(
      "TAR_BASE256_SIZE_NOT_SUPPORTED",
      `TAR entry "${entryName}" uses GNU base-256 size encoding (file > 8 GiB). Not supported in first pass.`
    );
    this.name = "TarBase256SizeNotSupportedError";
  }
};
var TarLongNameNotSupportedError = class extends WebcvtError {
  constructor(name, length) {
    super(
      "TAR_LONG_NAME_NOT_SUPPORTED",
      `TAR entry name "${name}" is ${length} bytes; names over 100 bytes require PAX/GNU extensions (deferred to Phase 4.5).`
    );
    this.name = "TarLongNameNotSupportedError";
  }
};
var TarCorruptStreamError = class extends WebcvtError {
  constructor(reason) {
    super("TAR_CORRUPT_STREAM", `TAR stream is corrupt: ${reason}`);
    this.name = "TarCorruptStreamError";
  }
};
var TarInvalidOctalFieldError = class extends WebcvtError {
  constructor(field) {
    super("TAR_INVALID_OCTAL_FIELD", `TAR header field "${field}" contains non-octal characters.`);
    this.name = "TarInvalidOctalFieldError";
  }
};
var TarCumulativeSizeCapError = class extends WebcvtError {
  constructor(total, cap) {
    super(
      "TAR_CUMULATIVE_SIZE_CAP",
      `Cumulative uncompressed size ${total} exceeds the total cap of ${cap} bytes (512 MiB).`
    );
    this.name = "TarCumulativeSizeCapError";
  }
};
var GzipInvalidMagicError = class extends WebcvtError {
  constructor() {
    super("GZIP_INVALID_MAGIC", "Not a valid gzip stream: wrong magic bytes.");
    this.name = "GzipInvalidMagicError";
  }
};
var GzipUnsupportedMethodError = class extends WebcvtError {
  constructor(method) {
    super(
      "GZIP_UNSUPPORTED_METHOD",
      `Gzip uses unsupported compression method ${method}; only method 8 (Deflate) is supported.`
    );
    this.name = "GzipUnsupportedMethodError";
  }
};
var GzipMultiMemberNotSupportedError = class extends WebcvtError {
  constructor() {
    super(
      "GZIP_MULTI_MEMBER_NOT_SUPPORTED",
      "Multi-member gzip files are not supported in first pass. Support is planned for Phase 4.5."
    );
    this.name = "GzipMultiMemberNotSupportedError";
  }
};
var ArchiveEncodeNotImplementedError = class extends WebcvtError {
  constructor(reason) {
    super(
      "ARCHIVE_ENCODE_NOT_IMPLEMENTED",
      `Archive encode not implemented: ${reason}. Install @catlabtech/webcvt-backend-wasm for transcode support.`
    );
    this.name = "ArchiveEncodeNotImplementedError";
  }
};

// src/compression.ts
async function collectStream(stream) {
  const chunks = [];
  let totalLength = 0;
  const reader = stream.getReader();
  try {
    while (true) {
      const result = await reader.read();
      if (result.done) break;
      chunks.push(result.value);
      totalLength += result.value.length;
    }
  } finally {
    reader.releaseLock();
  }
  if (chunks.length === 0) return new Uint8Array(0);
  if (chunks.length === 1) return chunks[0];
  const out = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.length;
  }
  return out;
}
function makeSizeCapTransform(entryName, perEntryCap, cumulativeRef) {
  let seen = 0;
  return new TransformStream({
    transform(chunk, controller) {
      seen += chunk.length;
      cumulativeRef.current += chunk.length;
      if (seen > perEntryCap) {
        controller.error(new ArchiveEntrySizeCapError(entryName, seen, perEntryCap));
        return;
      }
      if (cumulativeRef.current > cumulativeRef.cap) {
        controller.error(new ArchiveTotalSizeCapError(cumulativeRef.current, cumulativeRef.cap));
        return;
      }
      controller.enqueue(chunk);
    }
  });
}
function bytesToStream(data) {
  return new ReadableStream({
    start(controller) {
      controller.enqueue(data);
      controller.close();
    }
  });
}
async function decompressBytes(compressed, algorithm, entryName, cumulativeState) {
  const sourceStream = bytesToStream(compressed);
  const decomp = new DecompressionStream(algorithm);
  const capTransform = makeSizeCapTransform(
    entryName,
    MAX_ENTRY_UNCOMPRESSED_BYTES,
    cumulativeState
  );
  const outputStream = sourceStream.pipeThrough(decomp).pipeThrough(capTransform);
  return collectStream(outputStream);
}
async function compressBytes(data, algorithm) {
  const sourceStream = bytesToStream(data);
  const compStream = new CompressionStream(algorithm);
  return collectStream(
    sourceStream.pipeThrough(compStream)
  );
}
function decompressStream(compressed, algorithm, entryName, cumulativeState) {
  const sourceStream = bytesToStream(compressed);
  const decomp = new DecompressionStream(algorithm);
  const capTransform = makeSizeCapTransform(
    entryName,
    MAX_ENTRY_UNCOMPRESSED_BYTES,
    cumulativeState
  );
  return sourceStream.pipeThrough(decomp).pipeThrough(capTransform);
}

// src/crc32.ts
var POLY = 3988292384;
var TABLE = new Uint32Array(256);
for (let i = 0; i < 256; i++) {
  let r = i;
  for (let j = 0; j < 8; j++) {
    r = (r & 1) !== 0 ? r >>> 1 ^ POLY : r >>> 1;
  }
  TABLE[i] = r >>> 0;
}
function computeCrc32(data, seed = 4294967295) {
  let crc = seed;
  for (let i = 0; i < data.length; i++) {
    const byte = data[i] ?? 0;
    crc = (TABLE[(crc ^ byte) & 255] ?? 0) ^ crc >>> 8;
  }
  return (crc ^ 4294967295) >>> 0;
}

// src/path-validator.ts
var DRIVE_LETTER_RE = /^[A-Za-z]:/;
function validateEntryName(name) {
  if (name.includes("\0")) {
    throw new ArchiveInvalidEntryNameError(name, "name contains NUL byte");
  }
  const normalized = name.replace(/\\/g, "/");
  if (normalized.startsWith("/")) {
    throw new ArchiveInvalidEntryNameError(normalized, "absolute path (leading /)");
  }
  if (DRIVE_LETTER_RE.test(normalized)) {
    throw new ArchiveInvalidEntryNameError(normalized, "Windows drive-letter path");
  }
  const parts = normalized.split("/");
  for (const part of parts) {
    if (part === "..") {
      throw new ArchiveInvalidEntryNameError(normalized, 'path traversal via ".." segment');
    }
  }
  return normalized;
}

// src/zip-headers.ts
function decodeMsDosDateTime(dosTime, dosDate) {
  if (dosTime === 0 && dosDate === 0) {
    return /* @__PURE__ */ new Date("1980-01-01T00:00:00Z");
  }
  const second = (dosTime & 31) * 2;
  const minute = dosTime >> 5 & 63;
  const hour = dosTime >> 11 & 31;
  const day = dosDate & 31;
  const month = dosDate >> 5 & 15;
  const year = (dosDate >> 9 & 127) + 1980;
  return new Date(Date.UTC(year, month - 1, day, hour, minute, second));
}
function encodeMsDosDateTime(date) {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1;
  const day = date.getUTCDate();
  const hour = date.getUTCHours();
  const minute = date.getUTCMinutes();
  const second = date.getUTCSeconds();
  const dosDate = (year - 1980 & 127) << 9 | (month & 15) << 5 | day & 31;
  const dosTime = (hour & 31) << 11 | (minute & 63) << 5 | Math.floor(second / 2) & 31;
  return [dosTime, dosDate];
}
function encodeLocalFileHeader(out, offset, nameBytes, h) {
  const view = new DataView(out.buffer, out.byteOffset, out.byteLength);
  view.setUint32(offset + 0, ZIP_LOCAL_HEADER_SIG, true);
  view.setUint16(offset + 4, ZIP_VERSION_NEEDED, true);
  view.setUint16(offset + 6, ZIP_FLAG_UTF8, true);
  view.setUint16(offset + 8, h.method, true);
  view.setUint16(offset + 10, h.dosTime, true);
  view.setUint16(offset + 12, h.dosDate, true);
  view.setUint32(offset + 14, h.crc32, true);
  view.setUint32(offset + 18, h.compressedSize, true);
  view.setUint32(offset + 22, h.uncompressedSize, true);
  view.setUint16(offset + 26, nameBytes.length, true);
  view.setUint16(offset + 28, 0, true);
  out.set(nameBytes, offset + ZIP_LOCAL_HEADER_FIXED_SIZE);
}
function decodeCentralDirHeader(buf, offset) {
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  return {
    versionMadeBy: view.getUint16(offset + 4, true),
    versionNeeded: view.getUint16(offset + 6, true),
    flags: view.getUint16(offset + 8, true),
    method: view.getUint16(offset + 10, true),
    dosTime: view.getUint16(offset + 12, true),
    dosDate: view.getUint16(offset + 14, true),
    crc32: view.getUint32(offset + 16, true),
    compressedSize: view.getUint32(offset + 20, true),
    uncompressedSize: view.getUint32(offset + 24, true),
    fileNameLength: view.getUint16(offset + 28, true),
    extraFieldLength: view.getUint16(offset + 30, true),
    fileCommentLength: view.getUint16(offset + 32, true),
    diskNumberStart: view.getUint16(offset + 34, true),
    internalAttrs: view.getUint16(offset + 36, true),
    externalAttrs: view.getUint32(offset + 38, true),
    localHeaderOffset: view.getUint32(offset + 42, true)
  };
}
function encodeCentralDirHeader(out, offset, nameBytes, h) {
  const view = new DataView(out.buffer, out.byteOffset, out.byteLength);
  view.setUint32(offset + 0, ZIP_CENTRAL_DIR_SIG, true);
  view.setUint16(offset + 4, ZIP_VERSION_MADE_BY, true);
  view.setUint16(offset + 6, ZIP_VERSION_NEEDED, true);
  view.setUint16(offset + 8, ZIP_FLAG_UTF8, true);
  view.setUint16(offset + 10, h.method, true);
  view.setUint16(offset + 12, h.dosTime, true);
  view.setUint16(offset + 14, h.dosDate, true);
  view.setUint32(offset + 16, h.crc32, true);
  view.setUint32(offset + 20, h.compressedSize, true);
  view.setUint32(offset + 24, h.uncompressedSize, true);
  view.setUint16(offset + 28, nameBytes.length, true);
  view.setUint16(offset + 30, 0, true);
  view.setUint16(offset + 32, 0, true);
  view.setUint16(offset + 34, 0, true);
  view.setUint16(offset + 36, 0, true);
  const unixMode = h.isDirectory ? 16877 : 33188;
  view.setUint32(offset + 38, unixMode << 16 >>> 0, true);
  view.setUint32(offset + 42, h.localHeaderOffset, true);
  out.set(nameBytes, offset + ZIP_CENTRAL_DIR_FIXED_SIZE);
}
function encodeEocd(out, offset, h) {
  const view = new DataView(out.buffer, out.byteOffset, out.byteLength);
  view.setUint32(offset + 0, ZIP_EOCD_SIG, true);
  view.setUint16(offset + 4, 0, true);
  view.setUint16(offset + 6, 0, true);
  view.setUint16(offset + 8, h.numberOfRecords, true);
  view.setUint16(offset + 10, h.numberOfRecords, true);
  view.setUint32(offset + 12, h.centralDirectorySize, true);
  view.setUint32(offset + 16, h.centralDirectoryOffset, true);
  view.setUint16(offset + 20, 0, true);
}
function readU32LE(buf, offset) {
  if (offset + 4 > buf.length) return 0;
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  return view.getUint32(offset, true);
}
var UTF8_DECODER = new TextDecoder("utf-8");
var UTF8_ENCODER = new TextEncoder();

// src/zip-parser.ts
function findEocd(buf) {
  const minOffset = Math.max(0, buf.length - ZIP_EOCD_FIXED_SIZE - MAX_ZIP_COMMENT_BYTES);
  for (let i = buf.length - ZIP_EOCD_FIXED_SIZE; i >= minOffset; i--) {
    if (readU32LE(buf, i) === ZIP_EOCD_SIG) {
      return i;
    }
  }
  return -1;
}
function makeEntryAccessors(input, localHeaderOffset, cdCompressedSize, cdUncompressedSize, method, expectedCrc32, name, cumulativeState) {
  function getPayloadSlice() {
    const sig = readU32LE(input, localHeaderOffset);
    if (sig !== ZIP_LOCAL_HEADER_SIG) {
      throw new ZipBadLocalHeaderError(localHeaderOffset, sig);
    }
    const view = new DataView(input.buffer, input.byteOffset, input.byteLength);
    const localNameLen = view.getUint16(localHeaderOffset + 26, true);
    const localExtraLen = view.getUint16(localHeaderOffset + 28, true);
    const payloadOffset = localHeaderOffset + ZIP_LOCAL_HEADER_FIXED_SIZE + localNameLen + localExtraLen;
    if (payloadOffset + cdCompressedSize > input.length) {
      throw new ZipTruncatedEntryError(name, payloadOffset, cdCompressedSize, input.length);
    }
    return input.subarray(payloadOffset, payloadOffset + cdCompressedSize);
  }
  const data = async () => {
    const compressed = getPayloadSlice();
    if (method === ZIP_METHOD_STORED) {
      const crc2 = computeCrc32(compressed);
      if (crc2 !== expectedCrc32) {
        throw new ZipChecksumError(name, expectedCrc32, crc2);
      }
      return compressed;
    }
    const decompressed = await decompressBytes(compressed, "deflate-raw", name, cumulativeState);
    if (decompressed.length !== cdUncompressedSize) {
      throw new ZipChecksumError(name, expectedCrc32, computeCrc32(decompressed));
    }
    const crc = computeCrc32(decompressed);
    if (crc !== expectedCrc32) {
      throw new ZipChecksumError(name, expectedCrc32, crc);
    }
    return decompressed;
  };
  const stream = () => {
    const compressed = getPayloadSlice();
    if (method === ZIP_METHOD_STORED) {
      return new ReadableStream({
        start(controller) {
          controller.enqueue(compressed);
          controller.close();
        }
      });
    }
    return decompressStream(compressed, "deflate-raw", name, cumulativeState);
  };
  return { data, stream };
}
function parseZip(input) {
  if (input.length > MAX_INPUT_BYTES) {
    throw new ArchiveInputTooLargeError(input.length, MAX_INPUT_BYTES);
  }
  if (input.length < ZIP_EOCD_FIXED_SIZE) {
    throw new ZipTooShortError(input.length);
  }
  const eocdOffset = findEocd(input);
  if (eocdOffset === -1) {
    throw new ZipNoEocdError();
  }
  if (eocdOffset >= 4 && readU32LE(input, eocdOffset - 20) === 117853008) {
    throw new ZipNotZip64SupportedError();
  }
  const view = new DataView(input.buffer, input.byteOffset, input.byteLength);
  const numberOfThisDisk = view.getUint16(eocdOffset + 4, true);
  const diskWhereCdStarts = view.getUint16(eocdOffset + 6, true);
  const numberOfRecordsOnDisk = view.getUint16(eocdOffset + 8, true);
  const totalRecords = view.getUint16(eocdOffset + 10, true);
  const cdSize = view.getUint32(eocdOffset + 12, true);
  const cdOffset = view.getUint32(eocdOffset + 16, true);
  const commentLength = view.getUint16(eocdOffset + 20, true);
  if (commentLength > MAX_ZIP_COMMENT_BYTES) {
    throw new ZipCommentTooLargeError(commentLength);
  }
  if (numberOfThisDisk !== 0 || diskWhereCdStarts !== 0) {
    throw new ZipMultiDiskNotSupportedError(numberOfThisDisk);
  }
  if (numberOfRecordsOnDisk === ZIP64_SENTINEL_U16 || totalRecords === ZIP64_SENTINEL_U16 || cdSize === ZIP64_SENTINEL_U32 || cdOffset === ZIP64_SENTINEL_U32) {
    throw new ZipNotZip64SupportedError();
  }
  if (totalRecords > MAX_ZIP_ENTRIES) {
    throw new ZipTooManyEntriesError(totalRecords, MAX_ZIP_ENTRIES);
  }
  const commentBytes = input.subarray(eocdOffset + 22, eocdOffset + 22 + commentLength);
  const comment = UTF8_DECODER.decode(commentBytes);
  if (cdOffset + cdSize > eocdOffset) {
    throw new ZipCorruptStreamError(
      `Central directory (offset=${cdOffset}, size=${cdSize}) overruns EOCD (offset=${eocdOffset}).`
    );
  }
  const entries = [];
  const cumulativeState = { current: 0, cap: MAX_TOTAL_UNCOMPRESSED_BYTES };
  let cdPos = cdOffset;
  for (let i = 0; i < totalRecords; i++) {
    if (cdPos + ZIP_CENTRAL_DIR_FIXED_SIZE > input.length) {
      throw new ZipCorruptStreamError(`Central directory entry ${i} is truncated.`);
    }
    const sig = readU32LE(input, cdPos);
    if (sig !== ZIP_CENTRAL_DIR_SIG) {
      throw new ZipBadCentralDirectoryError(cdPos, sig);
    }
    const hdr = decodeCentralDirHeader(input, cdPos);
    if (hdr.diskNumberStart !== 0) {
      throw new ZipMultiDiskNotSupportedError(hdr.diskNumberStart);
    }
    if ((hdr.flags & ZIP_FLAG_ENCRYPTED) !== 0) {
      const nameSlice = input.subarray(
        cdPos + ZIP_CENTRAL_DIR_FIXED_SIZE,
        cdPos + ZIP_CENTRAL_DIR_FIXED_SIZE + Math.min(hdr.fileNameLength, 256)
      );
      const tempName = UTF8_DECODER.decode(nameSlice);
      throw new ZipEncryptedNotSupportedError(tempName);
    }
    if (hdr.method !== ZIP_METHOD_STORED && hdr.method !== ZIP_METHOD_DEFLATE) {
      const nameSlice = input.subarray(
        cdPos + ZIP_CENTRAL_DIR_FIXED_SIZE,
        cdPos + ZIP_CENTRAL_DIR_FIXED_SIZE + Math.min(hdr.fileNameLength, 256)
      );
      const tempName = UTF8_DECODER.decode(nameSlice);
      throw new ZipUnsupportedMethodError(tempName, hdr.method);
    }
    if (hdr.fileNameLength > MAX_ZIP_NAME_BYTES) {
      throw new ZipCorruptStreamError(
        `Entry ${i} has name length ${hdr.fileNameLength} > ${MAX_ZIP_NAME_BYTES}.`
      );
    }
    const nameOffset = cdPos + ZIP_CENTRAL_DIR_FIXED_SIZE;
    const nameBytes = input.subarray(nameOffset, nameOffset + hdr.fileNameLength);
    const rawName = UTF8_DECODER.decode(nameBytes);
    const name = validateEntryName(rawName);
    if (hdr.uncompressedSize > MAX_ENTRY_UNCOMPRESSED_BYTES) {
      throw new ArchiveEntrySizeCapError(name, hdr.uncompressedSize, MAX_ENTRY_UNCOMPRESSED_BYTES);
    }
    if (cumulativeState.current + hdr.uncompressedSize > MAX_TOTAL_UNCOMPRESSED_BYTES) {
      throw new ZipCorruptStreamError(
        `Cumulative uncompressed size would exceed ${MAX_TOTAL_UNCOMPRESSED_BYTES} bytes.`
      );
    }
    if (hdr.compressedSize > 0 && hdr.uncompressedSize > hdr.compressedSize * MAX_COMPRESSION_RATIO) {
      const ratio = Math.floor(hdr.uncompressedSize / hdr.compressedSize);
      throw new ZipCompressionRatioError(name, ratio);
    }
    const modified = decodeMsDosDateTime(hdr.dosTime, hdr.dosDate);
    const isDirectory = name.endsWith("/") && hdr.uncompressedSize === 0;
    const method = hdr.method;
    const { data, stream } = makeEntryAccessors(
      input,
      hdr.localHeaderOffset,
      hdr.compressedSize,
      hdr.uncompressedSize,
      method,
      hdr.crc32,
      name,
      cumulativeState
    );
    entries.push({
      name,
      method,
      crc32: hdr.crc32,
      compressedSize: hdr.compressedSize,
      uncompressedSize: hdr.uncompressedSize,
      modified,
      isDirectory,
      localHeaderOffset: hdr.localHeaderOffset,
      data,
      stream
    });
    cdPos += ZIP_CENTRAL_DIR_FIXED_SIZE + hdr.fileNameLength + hdr.extraFieldLength + hdr.fileCommentLength;
  }
  if (input.length > ZIP_EOCD_FIXED_SIZE && entries.length === 0 && totalRecords > 0) {
    throw new ZipCorruptStreamError("Non-empty input parsed to zero entries.");
  }
  return { entries, comment };
}

// src/zip-serializer.ts
async function serializeZip(file, opts) {
  if (file.entries.length > MAX_ZIP_ENTRIES) {
    throw new ZipTooManyEntriesError(file.entries.length, MAX_ZIP_ENTRIES);
  }
  const resolved = [];
  for (const entry of file.entries) {
    const nameBytes = UTF8_ENCODER.encode(entry.name);
    const [dosTime, dosDate] = encodeMsDosDateTime(entry.modified);
    if (entry.isDirectory) {
      resolved.push({
        nameBytes,
        compressedData: new Uint8Array(0),
        uncompressedSize: 0,
        crc32: 0,
        method: ZIP_METHOD_STORED,
        dosTime,
        dosDate,
        isDirectory: true
      });
      continue;
    }
    const rawBytes = await entry.data();
    const crc = computeCrc32(rawBytes);
    let method;
    if (opts?.method !== void 0) {
      method = opts.method;
    } else {
      method = rawBytes.length > ZIP_COMPRESS_THRESHOLD ? ZIP_METHOD_DEFLATE : ZIP_METHOD_STORED;
    }
    let compressedData;
    if (method === ZIP_METHOD_DEFLATE) {
      const deflated = await compressBytes(rawBytes, "deflate-raw");
      if (deflated.length >= rawBytes.length) {
        compressedData = rawBytes;
        method = ZIP_METHOD_STORED;
      } else {
        compressedData = deflated;
      }
    } else {
      compressedData = rawBytes;
    }
    resolved.push({
      nameBytes,
      compressedData,
      uncompressedSize: rawBytes.length,
      crc32: crc,
      method,
      dosTime,
      dosDate,
      isDirectory: false
    });
  }
  let totalSize = 0;
  const localOffsets = [];
  for (const r of resolved) {
    localOffsets.push(totalSize);
    totalSize += ZIP_LOCAL_HEADER_FIXED_SIZE + r.nameBytes.length + r.compressedData.length;
  }
  const centralDirOffset = totalSize;
  for (const r of resolved) {
    totalSize += ZIP_CENTRAL_DIR_FIXED_SIZE + r.nameBytes.length;
  }
  const centralDirSize = totalSize - centralDirOffset;
  totalSize += ZIP_EOCD_FIXED_SIZE;
  const out = new Uint8Array(totalSize);
  for (let i = 0; i < resolved.length; i++) {
    const r = resolved[i];
    const localOffset = localOffsets[i];
    encodeLocalFileHeader(out, localOffset, r.nameBytes, {
      method: r.method,
      dosTime: r.dosTime,
      dosDate: r.dosDate,
      crc32: r.crc32,
      compressedSize: r.compressedData.length,
      uncompressedSize: r.uncompressedSize
    });
    out.set(r.compressedData, localOffset + ZIP_LOCAL_HEADER_FIXED_SIZE + r.nameBytes.length);
  }
  let cdPos = centralDirOffset;
  for (let i = 0; i < resolved.length; i++) {
    const r = resolved[i];
    const localOffset = localOffsets[i];
    encodeCentralDirHeader(out, cdPos, r.nameBytes, {
      method: r.method,
      dosTime: r.dosTime,
      dosDate: r.dosDate,
      crc32: r.crc32,
      compressedSize: r.compressedData.length,
      uncompressedSize: r.uncompressedSize,
      localHeaderOffset: localOffset,
      isDirectory: r.isDirectory
    });
    cdPos += ZIP_CENTRAL_DIR_FIXED_SIZE + r.nameBytes.length;
  }
  encodeEocd(out, centralDirOffset + centralDirSize, {
    numberOfRecords: resolved.length,
    centralDirectorySize: centralDirSize,
    centralDirectoryOffset: centralDirOffset
  });
  return out;
}

// src/tar-parser.ts
var ASCII_DECODER = new TextDecoder("utf-8");
function parseOctal(buf, offset, length) {
  const firstByte = buf[offset] ?? 0;
  if ((firstByte & 128) !== 0) {
    throw new TarBase256SizeNotSupportedError("(unknown)");
  }
  const slice = buf.subarray(offset, offset + length);
  let end = slice.length;
  while (end > 0 && (slice[end - 1] === 0 || slice[end - 1] === 32)) {
    end--;
  }
  if (end === 0) return 0;
  const str = ASCII_DECODER.decode(slice.subarray(0, end));
  const value = Number.parseInt(str, 8);
  if (Number.isNaN(value)) {
    throw new TarInvalidOctalFieldError(str);
  }
  return value;
}
function computeChecksum(block) {
  let sum = 0;
  for (let i = 0; i < TAR_BLOCK_SIZE; i++) {
    if (i >= TAR_OFF_CHKSUM && i < TAR_OFF_CHKSUM + TAR_LEN_CHKSUM) {
      sum += 32;
    } else {
      sum += block[i] ?? 0;
    }
  }
  return sum;
}
function extractString(buf, offset, length) {
  const slice = buf.subarray(offset, offset + length);
  let end = slice.indexOf(0);
  if (end === -1) end = slice.length;
  return ASCII_DECODER.decode(slice.subarray(0, end));
}
function isAllZero(block, offset) {
  for (let i = offset; i < offset + TAR_BLOCK_SIZE; i++) {
    if ((block[i] ?? 0) !== 0) return false;
  }
  return true;
}
function ceilToBlock(size) {
  return TAR_BLOCK_SIZE * Math.ceil(size / TAR_BLOCK_SIZE);
}
function parseTar(input) {
  if (input.length > MAX_INPUT_BYTES) {
    throw new ArchiveInputTooLargeError(input.length, MAX_INPUT_BYTES);
  }
  if (input.length < TAR_BLOCK_SIZE * 2) {
    throw new TarTooShortError(input.length);
  }
  if (input.length % TAR_BLOCK_SIZE !== 0) {
    throw new TarMisalignedInputError(input.length);
  }
  const entries = [];
  let offset = 0;
  let cumulativeBytes = 0;
  while (offset + TAR_BLOCK_SIZE <= input.length) {
    const block = input.subarray(offset, offset + TAR_BLOCK_SIZE);
    if (isAllZero(block, 0)) {
      if (offset + TAR_BLOCK_SIZE * 2 <= input.length && isAllZero(input, offset + TAR_BLOCK_SIZE)) {
        break;
      }
      break;
    }
    extractString(block, TAR_OFF_MAGIC, TAR_LEN_MAGIC + TAR_LEN_VERSION);
    const magicOnly = extractString(block, TAR_OFF_MAGIC, TAR_LEN_MAGIC);
    const version = ASCII_DECODER.decode(
      block.subarray(TAR_OFF_VERSION, TAR_OFF_VERSION + TAR_LEN_VERSION)
    );
    const magicRaw = ASCII_DECODER.decode(
      block.subarray(TAR_OFF_MAGIC, TAR_OFF_MAGIC + TAR_LEN_MAGIC)
    );
    if (magicRaw === "ustar " || magicRaw.startsWith("ustar ")) {
      throw new TarGnuVariantNotSupportedError(offset);
    }
    if (magicOnly !== "ustar") {
      throw new TarNonUstarNotSupportedError(offset, magicOnly);
    }
    if (version !== TAR_VERSION) {
      throw new TarNonUstarNotSupportedError(offset, `${magicOnly}${version}`);
    }
    const storedChecksum = parseOctal(block, TAR_OFF_CHKSUM, TAR_LEN_CHKSUM);
    const computedChecksum = computeChecksum(block);
    if (storedChecksum !== computedChecksum) {
      throw new TarChecksumError(offset, storedChecksum, computedChecksum);
    }
    const typeflagByte = block[TAR_OFF_TYPEFLAG] ?? 0;
    const typeflag = String.fromCharCode(typeflagByte);
    if (typeflag === TAR_TYPEFLAG_PAX_EXTENDED || typeflag === TAR_TYPEFLAG_PAX_GLOBAL) {
      throw new TarPaxNotSupportedError(typeflag);
    }
    if (typeflag !== TAR_TYPEFLAG_FILE && typeflag !== TAR_TYPEFLAG_FILE_NUL && typeflag !== TAR_TYPEFLAG_DIRECTORY) {
      const entryName = extractString(block, TAR_OFF_NAME, TAR_LEN_NAME);
      throw new TarUnsupportedTypeflagError(typeflag, entryName);
    }
    const size = parseOctal(block, TAR_OFF_SIZE, TAR_LEN_SIZE);
    const rawName = extractString(block, TAR_OFF_NAME, TAR_LEN_NAME);
    const rawPrefix = extractString(block, TAR_OFF_PREFIX, TAR_LEN_PREFIX);
    const fullRawName = rawPrefix ? `${rawPrefix}/${rawName}` : rawName;
    const name = validateEntryName(fullRawName);
    if (name.length > MAX_TAR_NAME_BYTES) {
      throw new TarLongNameNotSupportedError(name, name.length);
    }
    const mode = parseOctal(block, TAR_OFF_MODE, TAR_LEN_MODE);
    const mtime = parseOctal(block, TAR_OFF_MTIME, TAR_LEN_MTIME);
    const modified = new Date(mtime * 1e3);
    const uname = extractString(block, TAR_OFF_UNAME, TAR_LEN_UNAME);
    const gname = extractString(block, TAR_OFF_GNAME, TAR_LEN_GNAME);
    const type = typeflag === TAR_TYPEFLAG_DIRECTORY ? "directory" : "file";
    if (size > MAX_ENTRY_UNCOMPRESSED_BYTES) {
      throw new ArchiveEntrySizeCapError(name, size, MAX_ENTRY_UNCOMPRESSED_BYTES);
    }
    cumulativeBytes += size;
    if (cumulativeBytes > MAX_TOTAL_UNCOMPRESSED_BYTES) {
      throw new TarCumulativeSizeCapError(cumulativeBytes, MAX_TOTAL_UNCOMPRESSED_BYTES);
    }
    if (entries.length >= MAX_TAR_ENTRIES) {
      throw new TarTooManyEntriesError(MAX_TAR_ENTRIES);
    }
    const dataOffset = offset + TAR_BLOCK_SIZE;
    const capturedInput = input;
    const capturedSize = size;
    const data = async () => {
      return capturedInput.subarray(dataOffset, dataOffset + capturedSize);
    };
    entries.push({ name, type, size, mode, modified, uname, gname, data });
    if (type === "directory" || size === 0) {
      offset += TAR_BLOCK_SIZE;
    } else {
      offset += TAR_BLOCK_SIZE + ceilToBlock(size);
    }
  }
  if (entries.length === 0 && input.length > TAR_BLOCK_SIZE * 2) {
    throw new TarCorruptStreamError(
      "Non-empty input produced zero entries. Archive may be corrupt."
    );
  }
  return { entries };
}

// src/tar-serializer.ts
var ASCII_ENCODER = new TextEncoder();
function writeOctal(block, offset, width, value) {
  const octalStr = value.toString(8).padStart(width - 1, "0");
  const bytes = ASCII_ENCODER.encode(octalStr);
  const copyLen = Math.min(bytes.length, width - 1);
  block.set(bytes.subarray(0, copyLen), offset + (width - 1 - copyLen));
  block[offset + width - 1] = 0;
}
function writeChecksum(block, checksum) {
  const octalStr = checksum.toString(8).padStart(6, "0");
  const bytes = ASCII_ENCODER.encode(octalStr);
  block.fill(0, TAR_OFF_CHKSUM, TAR_OFF_CHKSUM + TAR_LEN_CHKSUM);
  block.set(bytes.subarray(0, 6), TAR_OFF_CHKSUM);
  block[TAR_OFF_CHKSUM + 6] = 0;
  block[TAR_OFF_CHKSUM + 7] = 32;
}
function writeString(block, offset, maxLength, value) {
  const bytes = ASCII_ENCODER.encode(value);
  const copyLen = Math.min(bytes.length, maxLength);
  block.set(bytes.subarray(0, copyLen), offset);
}
function computeChecksum2(block) {
  let sum = 0;
  for (let i = 0; i < TAR_BLOCK_SIZE; i++) {
    if (i >= TAR_OFF_CHKSUM && i < TAR_OFF_CHKSUM + TAR_LEN_CHKSUM) {
      sum += 32;
    } else {
      sum += block[i] ?? 0;
    }
  }
  return sum;
}
function ceilToBlock2(size) {
  return TAR_BLOCK_SIZE * Math.ceil(size / TAR_BLOCK_SIZE);
}
async function serializeTar(file) {
  if (file.entries.length > MAX_TAR_ENTRIES) {
    throw new TarTooManyEntriesError(MAX_TAR_ENTRIES);
  }
  const resolvedEntries = [];
  for (const entry of file.entries) {
    const bytes = entry.type === "directory" ? new Uint8Array(0) : await entry.data();
    resolvedEntries.push({ entry, bytes });
  }
  let totalSize = 0;
  for (const { entry, bytes } of resolvedEntries) {
    totalSize += TAR_BLOCK_SIZE;
    if (entry.type === "file" && bytes.length > 0) {
      totalSize += ceilToBlock2(bytes.length);
    }
  }
  totalSize += TAR_BLOCK_SIZE * 2;
  const out = new Uint8Array(totalSize);
  let pos = 0;
  for (const { entry, bytes } of resolvedEntries) {
    if (entry.name.length > 100) {
      throw new TarLongNameNotSupportedError(entry.name, entry.name.length);
    }
    const header = new Uint8Array(TAR_BLOCK_SIZE);
    writeString(header, TAR_OFF_NAME, TAR_LEN_NAME, entry.name);
    writeOctal(
      header,
      TAR_OFF_MODE,
      TAR_LEN_MODE,
      entry.mode || (entry.type === "directory" ? 493 : 420)
    );
    writeOctal(header, 108, 8, 0);
    writeOctal(header, 116, 8, 0);
    writeOctal(header, TAR_OFF_MTIME, TAR_LEN_MTIME, Math.floor(entry.modified.getTime() / 1e3));
    writeOctal(header, TAR_OFF_SIZE, 12, entry.type === "directory" ? 0 : bytes.length);
    header[TAR_OFF_TYPEFLAG] = entry.type === "directory" ? TAR_TYPEFLAG_DIRECTORY.charCodeAt(0) : TAR_TYPEFLAG_FILE.charCodeAt(0);
    const magicBytes = ASCII_ENCODER.encode(TAR_MAGIC);
    header.set(magicBytes, TAR_OFF_MAGIC);
    const versionBytes = ASCII_ENCODER.encode(TAR_VERSION);
    header.set(versionBytes, TAR_OFF_VERSION);
    writeString(header, TAR_OFF_UNAME, TAR_LEN_UNAME, entry.uname);
    writeString(header, TAR_OFF_GNAME, TAR_LEN_GNAME, entry.gname);
    header.fill(32, TAR_OFF_CHKSUM, TAR_OFF_CHKSUM + TAR_LEN_CHKSUM);
    const checksum = computeChecksum2(header);
    writeChecksum(header, checksum);
    out.set(header, pos);
    pos += TAR_BLOCK_SIZE;
    if (entry.type === "file" && bytes.length > 0) {
      out.set(bytes, pos);
      pos += ceilToBlock2(bytes.length);
    }
  }
  return out;
}

// src/serializer.ts
async function decompressGzip(input) {
  const cumulativeState = { current: 0, cap: MAX_TOTAL_UNCOMPRESSED_BYTES };
  return decompressBytes(input, "gzip", "(gzip payload)", cumulativeState);
}
async function compressGzip(input) {
  return compressBytes(input, "gzip");
}

// src/parser.ts
function isZipMagic(buf) {
  return readU32LE(buf, 0) === ZIP_LOCAL_HEADER_SIG;
}
function isGzipMagic(buf) {
  return buf.length >= 2 && buf[0] === GZIP_MAGIC_0 && buf[1] === GZIP_MAGIC_1;
}
function isBz2Magic(buf) {
  if (buf.length < 3) return false;
  return buf[0] === BZ2_MAGIC[0] && buf[1] === BZ2_MAGIC[1] && buf[2] === BZ2_MAGIC[2];
}
function isXzMagic(buf) {
  if (buf.length < 6) return false;
  for (let i = 0; i < 6; i++) {
    if (buf[i] !== XZ_MAGIC[i]) return false;
  }
  return true;
}
function isTarMagic(buf) {
  if (buf.length < 263) return false;
  return buf[257] === 117 && // u
  buf[258] === 115 && // s
  buf[259] === 116 && // t
  buf[260] === 97 && // a
  buf[261] === 114 && // r
  buf[262] === 0;
}
function extractGzipFname(buf) {
  if (buf.length < 10) return void 0;
  const flg = buf[3] ?? 0;
  const FNAME_FLAG = 8;
  const FEXTRA_FLAG = 4;
  let pos = 10;
  if ((flg & FEXTRA_FLAG) !== 0) {
    if (pos + 2 > buf.length) return void 0;
    const xlen = (buf[pos] ?? 0) | (buf[pos + 1] ?? 0) << 8;
    pos += 2 + xlen;
  }
  if ((flg & FNAME_FLAG) !== 0) {
    const start = pos;
    while (pos < buf.length && buf[pos] !== 0) pos++;
    const nameBytes = buf.subarray(start, pos);
    const decoder = new TextDecoder("utf-8");
    return decoder.decode(nameBytes);
  }
  return void 0;
}
function makeGunzipCapTransform(cap) {
  let seen = 0;
  return new TransformStream({
    transform(chunk, controller) {
      seen += chunk.length;
      if (seen > cap) {
        controller.error(new ArchiveEntrySizeCapError("(gzip payload)", seen, cap));
        return;
      }
      controller.enqueue(chunk);
    }
  });
}
function detectMultiMemberGzip(input) {
  for (let i = 10; i <= input.length - 3; i++) {
    if (input[i] === 31 && input[i + 1] === 139 && input[i + 2] === 8) {
      throw new GzipMultiMemberNotSupportedError();
    }
  }
}
async function gunzip(input) {
  detectMultiMemberGzip(input);
  const source = new ReadableStream({
    start(controller) {
      controller.enqueue(input);
      controller.close();
    }
  });
  const decomp = new DecompressionStream("gzip");
  const capTransform = makeGunzipCapTransform(MAX_TOTAL_UNCOMPRESSED_BYTES);
  const decompressed = source.pipeThrough(decomp).pipeThrough(capTransform);
  return collectStream(decompressed);
}
async function parseArchive(input) {
  if (input.length > MAX_INPUT_BYTES) {
    throw new ArchiveInputTooLargeError(input.length, MAX_INPUT_BYTES);
  }
  if (isBz2Magic(input)) {
    throw new ArchiveBz2NotSupportedError();
  }
  if (isXzMagic(input)) {
    throw new ArchiveXzNotSupportedError();
  }
  if (isZipMagic(input)) {
    const file2 = parseZip(input);
    return { kind: "zip", file: file2 };
  }
  if (isGzipMagic(input)) {
    const originalName = extractGzipFname(input);
    const decompressed = await gunzip(input);
    if (isTarMagic(decompressed)) {
      const file2 = parseTar(decompressed);
      return { kind: "tar.gz", file: file2 };
    }
    return { kind: "gzip", payload: decompressed, originalName };
  }
  if (isTarMagic(input)) {
    const file2 = parseTar(input);
    return { kind: "tar", file: file2 };
  }
  const file = parseZip(input);
  return { kind: "zip", file };
}

// src/entry-iterator.ts
async function* iterateZip(file) {
  for (const entry of file.entries) {
    if (entry.isDirectory) continue;
    const data = await entry.data();
    yield { entry, data };
  }
}
async function* iterateZipAll(file) {
  for (const entry of file.entries) {
    if (entry.isDirectory) {
      yield { entry, data: null };
    } else {
      const data = await entry.data();
      yield { entry, data };
    }
  }
}
async function* iterateTar(file) {
  for (const entry of file.entries) {
    if (entry.type === "directory") continue;
    const data = await entry.data();
    yield { entry, data };
  }
}
async function* iterateTarAll(file) {
  for (const entry of file.entries) {
    if (entry.type === "directory") {
      yield { entry, data: null };
    } else {
      const data = await entry.data();
      yield { entry, data };
    }
  }
}

// src/cross.ts
function canCrossContainers(inputMime, outputMime) {
  return inputMime === ZIP_MIME && outputMime === TAR_MIME || inputMime === TAR_MIME && outputMime === ZIP_MIME;
}
function zipToTar(zip) {
  const entries = zip.entries.map(
    (entry) => ({
      name: entry.name,
      type: entry.isDirectory ? "directory" : "file",
      size: entry.uncompressedSize,
      // ZIP reader does not surface Unix mode; 0 lets serializeTar apply its
      // per-type default (0o755 dir / 0o644 file).
      mode: 0,
      modified: entry.modified,
      // ZIP has no owner user/group concept.
      uname: "",
      gname: "",
      data: entry.data
    })
  );
  return { entries };
}
function tarToZip(tar) {
  const entries = tar.entries.map((entry) => {
    const data = entry.data;
    return {
      name: entry.name,
      // Placeholders — serializeZip derives the real values from the bytes.
      method: 0,
      crc32: 0,
      compressedSize: 0,
      uncompressedSize: entry.size,
      modified: entry.modified,
      isDirectory: entry.type === "directory",
      localHeaderOffset: 0,
      data,
      stream: () => new ReadableStream({
        async start(controller) {
          controller.enqueue(await data());
          controller.close();
        }
      })
    };
  });
  return { entries, comment: "" };
}

// src/backend.ts
var SUPPORTED_MIMES = /* @__PURE__ */ new Set([ZIP_MIME, TAR_MIME, GZIP_MIME]);
var ArchiveBackend = class {
  name = "archive-zip";
  /**
   * Returns true for two routes (mirrors data-text Task 2):
   *   - identity: input.mime === output.mime AND the MIME is in the supported
   *     set. Byte-for-byte unchanged from the first pass.
   *   - cross-container: zip↔tar (canCrossContainers). gz never cross-converts.
   *
   * bz2/xz return false so BackendRegistry routes to backend-wasm.
   */
  async canHandle(input, output) {
    if (input.mime === BZ2_MIME || input.mime === XZ_MIME) return false;
    if (output.mime === BZ2_MIME || output.mime === XZ_MIME) return false;
    if (!SUPPORTED_MIMES.has(input.mime) || !SUPPORTED_MIMES.has(output.mime)) return false;
    if (input.mime === output.mime) return true;
    return canCrossContainers(input.mime, output.mime);
  }
  async convert(input, output, options) {
    const startMs = Date.now();
    if (input.size > MAX_INPUT_BYTES) {
      throw new ArchiveInputTooLargeError(input.size, MAX_INPUT_BYTES);
    }
    options.onProgress?.({ percent: 5, phase: "demux" });
    const inputBytes = new Uint8Array(await input.arrayBuffer());
    if (output.mime === ZIP_MIME && input.type === ZIP_MIME) {
      const zipFile = parseZip(inputBytes);
      options.onProgress?.({ percent: 50, phase: "mux" });
      const outputBytes = await serializeZip(zipFile);
      options.onProgress?.({ percent: 100, phase: "done" });
      const blob = new Blob([outputBytes.buffer], { type: output.mime });
      return {
        blob,
        format: output,
        durationMs: Date.now() - startMs,
        backend: this.name,
        hardwareAccelerated: false
      };
    }
    if (output.mime === TAR_MIME && input.type === TAR_MIME) {
      const tarFile = parseTar(inputBytes);
      options.onProgress?.({ percent: 50, phase: "mux" });
      const outputBytes = await serializeTar(tarFile);
      options.onProgress?.({ percent: 100, phase: "done" });
      const blob = new Blob([outputBytes.buffer], { type: output.mime });
      return {
        blob,
        format: output,
        durationMs: Date.now() - startMs,
        backend: this.name,
        hardwareAccelerated: false
      };
    }
    if (output.mime === GZIP_MIME && input.type === GZIP_MIME) {
      const decompressed = await decompressGzip(inputBytes);
      options.onProgress?.({ percent: 50, phase: "compress" });
      const outputBytes = await compressGzip(decompressed);
      options.onProgress?.({ percent: 100, phase: "done" });
      const blob = new Blob([outputBytes.buffer], { type: output.mime });
      return {
        blob,
        format: output,
        durationMs: Date.now() - startMs,
        backend: this.name,
        hardwareAccelerated: false
      };
    }
    if (output.mime === TAR_MIME && input.type === ZIP_MIME) {
      const zipFile = parseZip(inputBytes);
      options.onProgress?.({ percent: 45, phase: "remux" });
      const tarFile = zipToTar(zipFile);
      const outputBytes = await serializeTar(tarFile);
      options.onProgress?.({ percent: 100, phase: "done" });
      const blob = new Blob([outputBytes.buffer], { type: output.mime });
      return {
        blob,
        format: output,
        durationMs: Date.now() - startMs,
        backend: this.name,
        hardwareAccelerated: false
      };
    }
    if (output.mime === ZIP_MIME && input.type === TAR_MIME) {
      const tarFile = parseTar(inputBytes);
      options.onProgress?.({ percent: 45, phase: "remux" });
      const zipFile = tarToZip(tarFile);
      const outputBytes = await serializeZip(zipFile);
      options.onProgress?.({ percent: 100, phase: "done" });
      const blob = new Blob([outputBytes.buffer], { type: output.mime });
      return {
        blob,
        format: output,
        durationMs: Date.now() - startMs,
        backend: this.name,
        hardwareAccelerated: false
      };
    }
    throw new ArchiveEncodeNotImplementedError(
      `output MIME "${output.mime}" from input "${input.type}" is not supported; only identity round-trips are implemented`
    );
  }
};
var ZIP_FORMAT = {
  ext: "zip",
  mime: ZIP_MIME,
  category: "archive",
  description: "ZIP Archive (stored + Deflate)"
};
var TAR_FORMAT = {
  ext: "tar",
  mime: TAR_MIME,
  category: "archive",
  description: "POSIX ustar TAR Archive"
};
var GZIP_FORMAT = {
  ext: "gz",
  mime: GZIP_MIME,
  category: "archive",
  description: "GZip Compressed File"
};
var TGZ_FORMAT = {
  ext: "tgz",
  mime: TGZ_MIME,
  category: "archive",
  description: "GZip-compressed TAR Archive"
};
function registerArchiveBackend(registry = defaultRegistry) {
  const backend = new ArchiveBackend();
  registry.register(backend);
  return backend;
}

export { ArchiveBackend, ArchiveBz2NotSupportedError, ArchiveEncodeNotImplementedError, ArchiveEntrySizeCapError, ArchiveInputTooLargeError, ArchiveInvalidEntryNameError, ArchiveTotalSizeCapError, ArchiveXzNotSupportedError, GZIP_FORMAT, GzipInvalidMagicError, GzipMultiMemberNotSupportedError, GzipUnsupportedMethodError, TAR_FORMAT, TGZ_FORMAT, TarBase256SizeNotSupportedError, TarChecksumError, TarCorruptStreamError, TarCumulativeSizeCapError, TarGnuVariantNotSupportedError, TarInvalidOctalFieldError, TarLongNameNotSupportedError, TarMisalignedInputError, TarNonUstarNotSupportedError, TarPaxNotSupportedError, TarTooManyEntriesError, TarTooShortError, TarUnsupportedTypeflagError, ZIP_FORMAT, ZipBadCentralDirectoryError, ZipBadLocalHeaderError, ZipChecksumError, ZipCommentTooLargeError, ZipCompressionRatioError, ZipCorruptStreamError, ZipEncryptedNotSupportedError, ZipMultiDiskNotSupportedError, ZipNoEocdError, ZipNotZip64SupportedError, ZipTooManyEntriesError, ZipTooShortError, ZipTruncatedEntryError, ZipUnsupportedMethodError, canCrossContainers, compressGzip, decompressGzip, iterateTar, iterateTarAll, iterateZip, iterateZipAll, parseArchive, parseTar, parseZip, registerArchiveBackend, serializeTar, serializeZip, tarToZip, zipToTar };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map